import React, { useState, useEffect, useCallback } from 'react';
import Particles from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { collection, addDoc } from 'firebase/firestore';
import { db } from './firebase';
import posthog from './posthog';
import { initPostHog } from './posthog';
import './App.css';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Initialize PostHog
  useEffect(() => {
    initPostHog();
    // Track initial page view
    posthog.capture('$pageview');
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesConfig = {
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 120,
    interactivity: {
      events: {
        onClick: {
          enable: false,
        },
        onHover: {
          enable: false,
        },
        resize: true,
      },
      modes: {
        repulse: {
          distance: 100,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: ["#BB0000", "#006600", "#FFFFFF"],
      },
      links: {
        color: "#006600",
        distance: 150,
        enable: true,
        opacity: 0.2,
        width: 1,
      },
      collisions: {
        enable: true,
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 1,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 60,
      },
      opacity: {
        value: 0.3,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 3 },
      },
    },
    detectRetina: true,
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
    // Track section navigation
    posthog.capture('section_navigated', {
      section: sectionId,
      from: 'navigation'
    });
  };

  const toggleCard = (cardId) => {
    const isExpanding = !expandedCards[cardId];
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: isExpanding
    }));
    // Track card interaction
    posthog.capture('card_toggled', {
      card_id: cardId,
      action: isExpanding ? 'expanded' : 'collapsed'
    });
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    const formData = new FormData(e.target);
    const message = formData.get('message')?.trim();
    const email = formData.get('email')?.trim();
    
    // Validation
    if (!message) {
      setFormStatus({ 
        type: 'error', 
        message: 'Please enter a message before submitting.' 
      });
      setIsSubmitting(false);
      return;
    }
    
    if (message.length < 10) {
      setFormStatus({ 
        type: 'error', 
        message: 'Please provide more details in your message (at least 10 characters).' 
      });
      setIsSubmitting(false);
      return;
    }
    
    if (message.length > 5000) {
      setFormStatus({ 
        type: 'error', 
        message: 'Message is too long. Please keep it under 5000 characters.' 
      });
      setIsSubmitting(false);
      return;
    }
    
    // Basic email validation if provided
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormStatus({ 
        type: 'error', 
        message: 'Please enter a valid email address or leave it empty.' 
      });
      setIsSubmitting(false);
      return;
    }

    const feedbackData = {
      name: formData.get('name')?.trim() || 'Anonymous',
      email: email || '',
      category: formData.get('category'),
      message: message,
      siteID: 'fbill1',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'Direct'
    };

    try {
      await addDoc(collection(db, 'feedback'), feedbackData);
      setFormStatus({ 
        type: 'success', 
        message: 'Thank you for your feedback! We appreciate your input.' 
      });
      e.target.reset();
      
      // Track successful feedback submission
      posthog.capture('feedback_submitted', {
        category: feedbackData.category,
        has_email: !!feedbackData.email,
        message_length: feedbackData.message.length
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus({ type: '', message: '' });
      }, 5000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      // More specific error messages
      let errorMessage = 'Sorry, there was an error submitting your feedback. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your connection and try again.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your connection and try again.';
      }
      
      setFormStatus({ 
        type: 'error', 
        message: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ExpandableCard = ({ id, title, children, detailContent }) => (
    <div className={`highlight-card ${expandedCards[id] ? 'expanded' : ''}`}>
      <div className="card-content">
        <div className="card-header" onClick={() => toggleCard(id)}>
          <div className="card-main-content">
            <h4>{title}</h4>
            {children}
          </div>
          <button className="expand-btn">
            <span className={`expand-icon ${expandedCards[id] ? 'rotated' : ''}`}>+</span>
          </button>
        </div>
        <div className={`card-details ${expandedCards[id] ? 'visible' : ''}`}>
          {detailContent}
        </div>
      </div>
      <div className="card-footer">
        <button 
          className="learn-more-btn" 
          onClick={() => toggleCard(id)}
        >
          {expandedCards[id] ? 'Show Less' : 'Learn More'}
          <span className="learn-more-icon">{expandedCards[id] ? '↑' : '↓'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="nav-brand">
            <img src="/flag.webp" alt="Kenya Flag" className="nav-logo" />
            <h2>Finance Bill 2025</h2>
          </div>
          <button 
            className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li><a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>Home</a></li>
            <li><a href="#highlights" onClick={(e) => { e.preventDefault(); scrollToSection('highlights'); }}>Key Highlights</a></li>
            <li><a href="#impact" onClick={(e) => { e.preventDefault(); scrollToSection('impact'); }}>Impact</a></li>
            <li><a href="#timeline" onClick={(e) => { e.preventDefault(); scrollToSection('timeline'); }}>Timeline</a></li>
            <li><a href="#myths" onClick={(e) => { e.preventDefault(); scrollToSection('myths'); }}>Myths</a></li>
            <li><a href="#downloads" onClick={(e) => { e.preventDefault(); scrollToSection('downloads'); }}>Downloads</a></li>
            <li><a href="#qa" onClick={(e) => { e.preventDefault(); scrollToSection('qa'); }}>Q&A</a></li>
            <li><a href="#feedback" onClick={(e) => { e.preventDefault(); scrollToSection('feedback'); }}>Feedback</a></li>
            <li><a href="#chatbot" onClick={(e) => { e.preventDefault(); scrollToSection('chatbot'); }}>Learn More</a></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <Particles
          id="particles-hero"
          init={particlesInit}
          options={particlesConfig}
          className="particles-container"
        />
        <div className="hero-background"></div>
        <div className="container">
          <div className="hero-content fade-in">
            <span className="hero-badge">Progressive Reforms</span>
            <h1 className="hero-title">Finance Bill 2025: The Complete Picture</h1>
            <p className="hero-subtitle">
              Transformative tax reforms designed to stimulate economic growth, support businesses,
              and improve the welfare of Kenyan citizens
            </p>
            <div className="hero-stats">
              <div className="stat-card">
                <h3>500%</h3>
                <p>Increase in tax-free per diem allowance</p>
              </div>
              <div className="stat-card">
                <h3>100%</h3>
                <p>Tax exemption on retirement benefits</p>
              </div>
              <div className="stat-card">
                <h3>50%</h3>
                <p>Reduction in digital asset tax</p>
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => {
              scrollToSection('highlights');
              posthog.capture('cta_clicked', {
                cta_text: 'Explore Key Reforms',
                location: 'hero_section'
              });
            }}>
              Explore Key Reforms
            </button>
          </div>
        </div>
      </section>

      {/* Red Ribbon Section */}
      <section className="ribbon-section">
        <div className="ribbon-container">
          <div className="ribbon-content">
            <div className="ribbon-main">
              <span className="ribbon-icon">💡</span>
              <h3>Get the Facts. Know Your Rights. Claim Your Benefits.</h3>
              <span className="ribbon-icon">💡</span>
            </div>
            <div className="ribbon-subtext">
              <p>
                While social media erupts with controversy, the real Finance Bill 2025 tells a different story. 
                Hidden within 60 clauses are reforms designed to put more money in your pocket and protect what you've earned. 
                <strong>We've done the homework. Now it's time you knew the truth.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights Section */}
      <section id="highlights" className="section">
        <div className="container">
          <h2 className="section-title">Key Highlights</h2>
          <p className="section-subtitle">Click on any card to learn more about how these reforms benefit you</p>

          <div className="highlights-categories">
            {/* For Employees */}
            <div className="highlight-category fade-in">
              <div className="category-header">
                <span className="category-icon">👷</span>
                <h3>For Employees & Workers</h3>
              </div>
              <div className="highlights-grid">
                <ExpandableCard
                  id="per-diem"
                  title="Tax-Free Per Diem Boost"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>What This Means:</h5>
                        <p>Private sector employees can now receive up to KSh 10,000 per day in tax-free allowances for work-related travel, up from the previous KSh 2,000 limit.</p>
                      </div>
                      <div className="detail-section">
                        <h5>Who Benefits:</h5>
                        <ul>
                          <li>Sales representatives traveling for client meetings</li>
                          <li>Consultants working on different project sites</li>
                          <li>Field workers and technicians</li>
                          <li>Any employee with work-related travel expenses</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Impact:</h5>
                        <p>This achieves parity with public sector benefits and significantly enhances disposable income for employees who travel for work.</p>
                      </div>
                    </div>
                  }
                >
                  <div className="highlight-detail">
                    <span className="old-value">KSh 2,000</span>
                    <span className="arrow">→</span>
                    <span className="new-value">KSh 10,000</span>
                  </div>
                  <p>Daily allowance for private sector employees</p>
                </ExpandableCard>

                <ExpandableCard
                  id="retirement"
                  title="Retirement Protection"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Complete Tax Exemption:</h5>
                        <p>All gratuity payments are now 100% tax-exempt, protecting your retirement savings and ensuring dignity in your golden years.</p>
                      </div>
                      <div className="detail-section">
                        <h5>Coverage:</h5>
                        <ul>
                          <li>Both public and private sector employees</li>
                          <li>All forms of gratuity payments</li>
                          <li>End-of-service benefits</li>
                          <li>Retirement lump sum payments</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Why It Matters:</h5>
                        <p>This reform ensures that workers can keep 100% of their hard-earned retirement benefits without any tax deductions, providing financial security for retirees.</p>
                      </div>
                    </div>
                  }
                >
                  <div className="highlight-percentage">100%</div>
                  <p>Complete tax exemption on all gratuity payments</p>
                </ExpandableCard>

                <ExpandableCard
                  id="mortgage"
                  title="Enhanced Mortgage Relief"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Expanded Coverage:</h5>
                        <p>The KSh 360,000 annual tax deduction now covers both home purchases AND home construction, making homeownership more accessible.</p>
                      </div>
                      <div className="detail-section">
                        <h5>What You Can Claim:</h5>
                        <ul>
                          <li>Mortgage interest on home purchases</li>
                          <li>Construction loan interest</li>
                          <li>Building materials and labor costs</li>
                          <li>Architect and contractor fees</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Supporting Affordable Housing:</h5>
                        <p>This aligns with Kenya's affordable housing agenda, making it easier for middle-class families to build or buy homes.</p>
                      </div>
                    </div>
                  }
                >
                  <div className="highlight-amount">KSh 360,000</div>
                  <p>Annual tax deduction now includes home construction</p>
                </ExpandableCard>
              </div>
            </div>

            {/* For Businesses */}
            <div className="highlight-category fade-in">
              <div className="category-header">
                <span className="category-icon">💼</span>
                <h3>Business & Investment</h3>
              </div>
              <div className="highlights-grid">
                <ExpandableCard
                  id="asset-writeoffs"
                  title="Immediate Asset Write-offs"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Simplified Depreciation:</h5>
                        <p>Businesses can now claim 100% deduction for tools and equipment in the year of purchase, eliminating complex 3-year depreciation schedules.</p>
                      </div>
                      <div className="detail-section">
                        <h5>Eligible Items:</h5>
                        <ul>
                          <li>Manufacturing equipment and machinery</li>
                          <li>Office furniture and computers</li>
                          <li>Vehicles used for business</li>
                          <li>Professional tools and instruments</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Cash Flow Benefits:</h5>
                        <p>This significantly improves cash flow for SMEs, allowing them to reinvest savings into growth and expansion immediately.</p>
                      </div>
                    </div>
                  }
                >
                  <div className="highlight-percentage">100%</div>
                  <p>Full deduction for tools and equipment in year of purchase</p>
                </ExpandableCard>

                <ExpandableCard
                  id="digital-assets"
                  title="Digital Asset Tax Cut"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Reduced Tax Rate:</h5>
                        <p>Digital asset transfer tax reduced from 3% to 1.5%, making Kenya more competitive in the global digital economy.</p>
                      </div>
                      <div className="detail-section">
                        <h5>What This Covers:</h5>
                        <ul>
                          <li>Cryptocurrency transactions</li>
                          <li>Blockchain-based asset transfers</li>
                          <li>Digital token exchanges</li>
                          <li>NFT and digital art transactions</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Strategic Impact:</h5>
                        <p>Positions Kenya as a fintech leader in Africa, encouraging innovation and attracting digital economy investments.</p>
                      </div>
                    </div>
                  }
                >
                  <div className="highlight-detail">
                    <span className="old-value">3%</span>
                    <span className="arrow">→</span>
                    <span className="new-value">1.5%</span>
                  </div>
                  <p>Promoting blockchain and fintech adoption</p>
                </ExpandableCard>

                <ExpandableCard
                  id="nifc-benefits"
                  title="NIFC Tax Benefits"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Major Investors (KSh 3 billion+):</h5>
                        <ul>
                          <li>15% corporate tax rate for first 10 years</li>
                          <li>20% for subsequent 10 years</li>
                          <li>Creates high-value employment opportunities</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Startups & SMEs:</h5>
                        <ul>
                          <li>15% tax rate for first 3 years</li>
                          <li>20% for next 4 years</li>
                          <li>Nurtures innovation ecosystem</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Economic Impact:</h5>
                        <p>The Nairobi International Financial Centre becomes a globally competitive hub, attracting international investment and creating thousands of jobs.</p>
                      </div>
                    </div>
                  }
                >
                  <div className="highlight-percentage">15%</div>
                  <p>Corporate tax rate for major investors (first 10 years)</p>
                </ExpandableCard>
              </div>
            </div>

            {/* Infrastructure */}
            <div className="highlight-category fade-in">
              <div className="category-header">
                <span className="category-icon">🏗️</span>
                <h3>Infrastructure & Development</h3>
              </div>
              <div className="highlights-grid">
                <ExpandableCard
                  id="sports"
                  title="Sports Development"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Full Tax Deduction:</h5>
                        <p>100% tax deduction available for constructing public sports facilities, encouraging private sector investment in community infrastructure.</p>
                      </div>
                      <div className="detail-section">
                        <h5>Eligible Facilities:</h5>
                        <ul>
                          <li>Football stadiums and pitches</li>
                          <li>Basketball and volleyball courts</li>
                          <li>Swimming pools and gyms</li>
                          <li>Athletics tracks and fields</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Community Impact:</h5>
                        <p>Supports youth development, national talent discovery, and promotes healthy lifestyles across communities.</p>
                      </div>
                    </div>
                  }
                >
                  <p>Full tax deduction for constructing public sports facilities</p>
                </ExpandableCard>

                <ExpandableCard
                  id="tourism"
                  title="Tourism Boost"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Special VAT Treatment:</h5>
                        <p>Reduced VAT rates for tourism facility construction, valid until June 30, 2026.</p>
                      </div>
                      <div className="detail-section">
                        <h5>Covered Facilities:</h5>
                        <ul>
                          <li>Hotels and lodges</li>
                          <li>Recreational parks (50+ acres)</li>
                          <li>Conference and convention centers</li>
                          <li>Tourist attraction developments</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Economic Benefits:</h5>
                        <p>Accelerates tourism infrastructure development, creating jobs and boosting Kenya's position as a premier tourist destination.</p>
                      </div>
                    </div>
                  }
                >
                  <p>Special VAT treatment for tourism facility construction until 2026</p>
                </ExpandableCard>

                <ExpandableCard
                  id="manufacturing"
                  title="Manufacturing Support"
                  detailContent={
                    <div className="detail-content">
                      <div className="detail-section">
                        <h5>Reduced Export Levy:</h5>
                        <p>Export & Investment Promotion Levy reduced from 17.5% to 10% for steel and iron products.</p>
                      </div>
                      <div className="detail-section">
                        <h5>Industry Benefits:</h5>
                        <ul>
                          <li>Lower production costs for manufacturers</li>
                          <li>Enhanced competitiveness in regional markets</li>
                          <li>Increased export potential</li>
                          <li>Job creation in manufacturing sector</li>
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h5>Strategic Impact:</h5>
                        <p>Strengthens Kenya's manufacturing base and supports the Big Four Agenda's manufacturing pillar.</p>
                      </div>
                    </div>
                  }
                >
                  <div className="highlight-detail">
                    <span className="old-value">17.5%</span>
                    <span className="arrow">→</span>
                    <span className="new-value">10%</span>
                  </div>
                  <p>Export levy on steel and iron products</p>
                </ExpandableCard>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Administrative Reforms */}
      <section className="section admin-section">
        <div className="container">
          <h2 className="section-title">Administrative Reforms</h2>
          <div className="admin-grid">
            <div className="admin-card fade-in">
              <div className="admin-icon">⚡</div>
              <h3>Faster Processing</h3>
              <ul>
                <li>6-month deadline for accounting year changes</li>
                <li>14-day processing for excise duty licenses</li>
                <li>Automatic approval if no response</li>
              </ul>
            </div>
            <div className="admin-card fade-in">
              <div className="admin-icon">🛡️</div>
              <h3>Taxpayer Protection</h3>
              <ul>
                <li>Waiver for e-tax system errors</li>
                <li>Written reasons for assessments</li>
                <li>No double taxation on compliant payments</li>
              </ul>
            </div>
            <div className="admin-card fade-in">
              <div className="admin-icon">🤝</div>
              <h3>Fair Treatment</h3>
              <ul>
                <li>Transparent assessment process</li>
                <li>Protection from duplicate charges</li>
                <li>Clear penalty applications</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="section impact-section">
        <div className="container">
          <h2 className="section-title">Why These Reforms Matter</h2>

          <div className="impact-grid">
            <div className="impact-card fade-in">
              <div className="impact-header">
                <span className="impact-icon">👥</span>
                <h3>For Citizens</h3>
              </div>
              <ul className="impact-list">
                <li>More disposable income through increased allowances</li>
                <li>Protected retirement savings with full tax exemption</li>
                <li>Better access to homeownership with expanded relief</li>
                <li>Improved community facilities through private investment</li>
              </ul>
            </div>

            <div className="impact-card fade-in">
              <div className="impact-header">
                <span className="impact-icon">🏢</span>
                <h3>For Businesses</h3>
              </div>
              <ul className="impact-list">
                <li>Improved cash flow through immediate deductions</li>
                <li>Reduced compliance burden with simpler processes</li>
                <li>Clear investment incentives for long-term growth</li>
                <li>Predictable tax environment with advance agreements</li>
              </ul>
            </div>

            <div className="impact-card fade-in">
              <div className="impact-header">
                <span className="impact-icon">🇰🇪</span>
                <h3>For Kenya</h3>
              </div>
              <ul className="impact-list">
                <li>Enhanced competitiveness as investment destination</li>
                <li>Support for digital economy transformation</li>
                <li>Sustainable revenue generation through broader base</li>
                <li>Balanced economic growth across sectors</li>
              </ul>
            </div>
          </div>

          <div className="economic-impact fade-in">
            <h3>Economic Impact Summary</h3>
            <div className="impact-stats">
              <div className="impact-stat">
                <h4>Revenue Sustainability</h4>
                <p>Broader tax base through digital economy inclusion</p>
              </div>
              <div className="impact-stat">
                <h4>Investment Climate</h4>
                <p>Clear incentives for strategic sector development</p>
              </div>
              <div className="impact-stat">
                <h4>Social Development</h4>
                <p>Protection of vulnerable citizens and communities</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="timeline" className="section timeline-section">
        <div className="container">
          <h2 className="section-title">Implementation Timeline</h2>
          <div className="timeline">
            <div className="timeline-items">
              <div className="timeline-item fade-in">
                <div className="timeline-date">July 1, 2025</div>
                <div className="timeline-content">
                  <h4>Early Implementation</h4>
                  <ul>
                    <li>Advance Pricing Agreements activate</li>
                    <li>System error penalty waivers begin</li>
                  </ul>
                </div>
              </div>
              <div className="timeline-item fade-in">
                <div className="timeline-date">January 1, 2026</div>
                <div className="timeline-content">
                  <h4>Full Implementation</h4>
                  <ul>
                    <li>All tax reforms take effect</li>
                    <li>Complete rollout of benefits</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Myths & Misinformation Section */}
      <section id="myths" className="section myths-section">
        <div className="container">
          <h2 className="section-title">Debunking Common Myths</h2>
          <p className="section-subtitle">Let's separate fact from fiction about the Finance Bill 2025</p>
          
          <div className="myths-grid">
            <div className="myth-card fade-in">
              <div className="myth-header">
                <span className="myth-icon">❌</span>
                <h3 className="myth-title">MYTH: "VAT increased from 16% to 18%"</h3>
              </div>
              <div className="myth-content">
                <h4 className="fact-label">✅ FACT:</h4>
                <p>VAT remains at 16% - unchanged. No clause in Finance Bill 2025 proposes any VAT rate change.</p>
                <div className="myth-source">
                  <strong>Why this myth exists:</strong> Confusion from EAC harmonization discussions and <a href="/documents/support/Medium-Term-Revenue-Strategy-2023.pdf" download className="doc-link" onClick={() => {
                    posthog.capture('document_downloaded', {
                      document: 'Medium-Term Revenue Strategy',
                      format: 'pdf',
                      section: 'myths'
                    });
                  }}>Medium-Term Revenue Strategy</a> proposals that were NOT adopted.
                </div>
                <div className="myth-verification">
                  <strong>Verified by:</strong> PwC Kenya, KPMG Analysis, Official Finance Bill text
                </div>
              </div>
            </div>

            <div className="myth-card fade-in">
              <div className="myth-header">
                <span className="myth-icon">❌</span>
                <h3 className="myth-title">MYTH: "5% tax on tea, coffee, and avocado sales"</h3>
              </div>
              <div className="myth-content">
                <h4 className="fact-label">✅ FACT:</h4>
                <p>NO direct sales tax on agricultural produce in The Bill. Farmers continue to enjoy agricultural income tax exemption.</p>
                <div className="myth-source">
                  <strong>Origin of confusion:</strong> <a href="/documents/support/Medium-Term-Revenue-Strategy-2023.pdf" download className="doc-link" onClick={() => {
                    posthog.capture('document_downloaded', {
                      document: 'MTRS 2023-2027',
                      format: 'pdf',
                      section: 'myths'
                    });
                  }}>MTRS 2023-2027</a> suggested 5% withholding tax on produce sold through cooperatives, but this proposal was NOT adopted in Finance Bill 2025.
                </div>
                <div className="myth-verification">
                  <strong>What actually changed:</strong> Some VAT changes on farm inputs, but NO direct crop sales tax
                </div>
              </div>
            </div>

            <div className="myth-card fade-in">
              <div className="myth-header">
                <span className="myth-icon">❌</span>
                <h3 className="myth-title">MYTH: "PAYE rates increased to 37.7%"</h3>
              </div>
              <div className="myth-content">
                <h4 className="fact-label">✅ FACT:</h4>
                <p>The Bill does not propose these changes. Current PAYE rates remain: 10% for low-income earners, 35% for highest band.</p>
                <div className="myth-source">
                  <strong>What's actually in the Bill:</strong> From July 1, 2025, employers must automatically apply tax reliefs for employees
                </div>
                <div className="myth-verification">
                  <strong>Source of misinformation:</strong> <a href="/documents/support/P508617-0037cc2b-2412-4d93-af21-b1a559f4c775-2.pdf" download className="doc-link" onClick={() => {
                    posthog.capture('document_downloaded', {
                      document: 'World Bank advisory reports',
                      format: 'pdf',
                      section: 'myths'
                    });
                  }}>World Bank advisory reports</a> suggesting higher rates - NOT included in Finance Bill 2025
                </div>
              </div>
            </div>

            <div className="myth-card fade-in">
              <div className="myth-header">
                <span className="myth-icon">❌</span>
                <h3 className="myth-title">MYTH: "Finance Bill destroys MSMEs and jobs"</h3>
              </div>
              <div className="myth-content">
                <h4 className="fact-label">✅ FACT:</h4>
                <p>The Bill offers tax incentives and support for MSMEs:</p>
                <ul>
                  <li>NIFCA startups: 15% tax for 3 years, 20% for next 4 years</li>
                  <li>Streamlines taxes and boosts digital growth</li>
                  <li>Encourages business formalization</li>
                  <li>Supports expanding funding and market growth</li>
                </ul>
                <div className="myth-verification">
                  <strong>Supporting evidence:</strong> <a href="/documents/support/Draft MSME Policy 2025.pdf" download className="doc-link" onClick={() => {
                    posthog.capture('document_downloaded', {
                      document: 'MSME Policy 2025',
                      format: 'pdf',
                      section: 'myths'
                    });
                  }}>MSME Policy 2025</a> aims to expand funding and create more jobs
                </div>
              </div>
            </div>

            <div className="myth-card fade-in">
              <div className="myth-header">
                <span className="myth-icon">❌</span>
                <h3 className="myth-title">MYTH: "10% tax on idle land"</h3>
              </div>
              <div className="myth-content">
                <h4 className="fact-label">✅ FACT:</h4>
                <p>NO 10% idle land tax exists in Finance Bill 2025. No clause references land taxation - property tax remains under county governments.</p>
                <div className="myth-source">
                  <strong>Origin of misinformation:</strong> Viral WhatsApp messages with no credible source or evidence.
                </div>
                <div className="myth-verification">
                  <strong>Verification:</strong> This claim has been widely circulated online but is not in the Bill. Always check official documents to avoid misinformation.
                </div>
              </div>
            </div>

            <div className="myth-card fade-in">
              <div className="myth-header">
                <span className="myth-icon">❌</span>
                <h3 className="myth-title">MYTH: "New 15% internet access tax"</h3>
              </div>
              <div className="myth-content">
                <h4 className="fact-label">✅ FACT:</h4>
                <p>There is no new 15% levy on internet access services. Digital asset tax remains at 1.5%.</p>
                <div className="myth-source">
                  <strong>What actually exists:</strong> Excise duty on digital services applies mainly to online content, gaming, and advertising - not general internet access.
                </div>
                <div className="myth-verification">
                  <strong>Actual changes:</strong> 1.5% Digital Service Tax replaced by 6% Significant Economic Presence Tax for non-residents via digital marketplaces
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section id="downloads" className="section downloads-section">
        <div className="container">
          <h2 className="section-title">Key Documents</h2>
          <p className="section-subtitle">Download official documents to learn more about the Finance Bill 2025</p>
          
          <div className="downloads-grid downloads-grid-two">
            <div className="download-card fade-in">
              <div className="download-icon">📄</div>
              <h3>Finance Bill 2025</h3>
              <p>The complete official Finance Bill document with all 60 clauses and provisions</p>
              <a href="/documents/finance-bill-2025.pdf" download className="btn btn-primary" onClick={() => {
                posthog.capture('document_downloaded', {
                  document: 'Finance Bill 2025',
                  format: 'pdf'
                });
              }}>
                Download PDF
              </a>
            </div>
            
            <div className="download-card fade-in">
              <div className="download-icon">📊</div>
              <h3>KPMG Analysis</h3>
              <p>Comprehensive professional analysis of the Finance Bill by KPMG tax experts</p>
              <a href="/documents/kpmg-analysis-2025.pdf" download className="btn btn-primary" onClick={() => {
                posthog.capture('document_downloaded', {
                  document: 'KPMG Analysis',
                  format: 'pdf'
                });
              }}>
                Download PDF
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Q&A Section */}
      <section id="qa" className="section qa-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Get answers to common questions about the Finance Bill 2025</p>
          
          <div className="qa-accordion">
            <details className="qa-item fade-in">
              <summary className="qa-question">
                <span className="qa-icon">❓</span>
                When will the Finance Bill 2025 take effect?
              </summary>
              <div className="qa-answer">
                <p>The Finance Bill 2025 has a phased implementation timeline:</p>
                <ul>
                  <li><strong>July 1, 2025:</strong> Early implementation of Advance Pricing Agreements and system error penalty waivers</li>
                  <li><strong>January 1, 2026:</strong> Full implementation of all tax reforms and benefits</li>
                </ul>
              </div>
            </details>
            
            <details className="qa-item fade-in">
              <summary className="qa-question">
                <span className="qa-icon">❓</span>
                How do I claim the increased per diem allowance?
              </summary>
              <div className="qa-answer">
                <p>The KSh 10,000 daily tax-free allowance is automatically applied by your employer for work-related travel. Ensure your employer is aware of the new limits and properly documents travel expenses.</p>
              </div>
            </details>
            
            <details className="qa-item fade-in">
              <summary className="qa-question">
                <span className="qa-icon">❓</span>
                What qualifies for the mortgage interest relief?
              </summary>
              <div className="qa-answer">
                <p>The KSh 360,000 annual deduction now covers:</p>
                <ul>
                  <li>Interest on home purchase mortgages</li>
                  <li>Interest on construction loans</li>
                  <li>Costs for building materials and labor</li>
                  <li>Professional fees (architects, contractors)</li>
                </ul>
              </div>
            </details>
            
            <details className="qa-item fade-in">
              <summary className="qa-question">
                <span className="qa-icon">❓</span>
                How does the digital asset tax reduction work?
              </summary>
              <div className="qa-answer">
                <p>The tax on digital asset transfers (including cryptocurrency) is reduced from 3% to 1.5% of the transaction value. This applies to all blockchain-based transactions, NFTs, and digital token exchanges.</p>
              </div>
            </details>
            
            <details className="qa-item fade-in">
              <summary className="qa-question">
                <span className="qa-icon">❓</span>
                What protection do I have against KRA system errors?
              </summary>
              <div className="qa-answer">
                <p>The Bill provides automatic waiver of penalties caused by e-tax system errors. If you experience system issues, document them and you won't be penalized for late submissions or payments due to technical problems.</p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <section id="feedback" className="section feedback-section">
        <div className="container">
          <h2 className="section-title">Share Your Feedback</h2>
          <p className="section-subtitle">Help us improve this platform and better serve Kenyans</p>
          
          <div className="feedback-content fade-in">
            <form 
              className="feedback-form"
              onSubmit={handleFeedbackSubmit}
            >
              <div className="form-group">
                <label htmlFor="name">Name (Optional)</label>
                <input type="text" id="name" name="name" placeholder="Your name" />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email (Optional)</label>
                <input type="email" id="email" name="email" placeholder="your.email@example.com" />
              </div>
              
              <div className="form-group">
                <label htmlFor="category">Feedback Category</label>
                <select id="category" name="category" required>
                  <option value="">Select a category</option>
                  <option value="general">General Feedback</option>
                  <option value="question">Question about The Bill</option>
                  <option value="suggestion">Suggestion for Improvement</option>
                  <option value="correction">Report an Error</option>
                  <option value="impact">Share Your Story</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">
                  Your Message
                  <span className="char-counter" style={{ 
                    float: 'right', 
                    fontSize: '0.875rem',
                    color: messageLength > 4900 ? '#e74c3c' : '#666'
                  }}>
                    {messageLength}/5000
                  </span>
                </label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows="5" 
                  placeholder="Share your thoughts, questions, or experiences..." 
                  required
                  maxLength="5000"
                  onChange={(e) => setMessageLength(e.target.value.length)}
                ></textarea>
              </div>
              
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
              
              {formStatus.message && (
                <div className={`form-status ${formStatus.type}`}>
                  {formStatus.message}
                </div>
              )}
            </form>
            
            <div className="feedback-info">
              <h3>Why Your Feedback Matters</h3>
              <p>Your input helps us:</p>
              <ul>
                <li>Improve the accuracy of information</li>
                <li>Add more relevant content</li>
                <li>Better explain complex provisions</li>
                <li>Address concerns and questions</li>
              </ul>
              <p className="feedback-note">All feedback is reviewed and helps shape future updates to this platform.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Chatbot CTA Section */}
      <section id="chatbot" className="section cta-section">
        <div className="container">
          <div className="cta-content fade-in">
            <h2 className="cta-title">Have Questions About the Finance Bill?</h2>
            <p className="cta-subtitle">
              Our AI-powered chatbot is here to help you understand how these reforms affect you personally.
              Get instant, personalized answers to all your questions.
            </p>
            <div className="cta-features">
              <div className="cta-feature">
                <span className="feature-icon">💬</span>
                <h4>Instant Answers</h4>
                <p>Get immediate responses to your specific queries</p>
              </div>
              <div className="cta-feature">
                <span className="feature-icon">📊</span>
                <h4>Personalized Analysis</h4>
                <p>Understand how The Bill impacts your situation</p>
              </div>
              <div className="cta-feature">
                <span className="feature-icon">🎯</span>
                <h4>Expert Guidance</h4>
                <p>Navigate complex provisions with ease</p>
              </div>
            </div>
            <a
              href="https://finance-bill-bot-production.up.railway.app"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-large"
              onClick={() => {
                posthog.capture('external_link_clicked', {
                  destination: 'ai_chatbot',
                  url: 'https://finance-bill-bot-production.up.railway.app'
                });
              }}
            >
              Chat with Our AI Assistant
            </a>
            <p className="cta-note">Available 24/7 • Free to use • No registration required</p>
          </div>
        </div>
      </section>

      {/* Affiliation Section */}
      <section className="affiliation-section">
        <div className="container">
          <div className="affiliation-content">
            <h3>In Partnership With</h3>
            <a 
              href="https://amkeni.ke" 
              target="_blank" 
              rel="noopener noreferrer"
              className="affiliation-link"
              onClick={() => {
                posthog.capture('external_link_clicked', {
                  destination: 'amkeni_ke',
                  url: 'https://amkeni.ke'
                });
              }}
            >
              <div className="affiliation-card">
                <h2 className="affiliation-logo">Amkeni.ke</h2>
                <p className="affiliation-tagline">Empowering Kenyan Citizens Through Information</p>
                <span className="visit-link">Visit Amkeni.ke →</span>
              </div>
            </a>
            <p className="affiliation-description">
              This platform is proudly affiliated with Amkeni.ke, a civic engagement initiative 
              dedicated to promoting transparency, accountability, and citizen participation in Kenya's democratic processes.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-main">
              <h3>Building a Prosperous Kenya Together</h3>
              <p>The Finance Bill 2025 represents a bold step toward creating a fair and efficient tax system
                that supports economic growth and protects vulnerable citizens.</p>
            </div>

            <div className="footer-cta">
              <div className="footer-learn-more">
                <h4>Want to Learn More?</h4>
                <p>Click on any highlight card above to discover detailed benefits, or chat with our AI assistant for personalized guidance.</p>
                <div className="footer-actions">
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => scrollToSection('highlights')}
                  >
                    Explore Benefits ↑
                  </button>
                  <a
                    href="https://finance-bill-bot-production.up.railway.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-small"
                  >
                    Ask AI Assistant
                  </a>
                </div>
              </div>
            </div>

            <div className="footer-flag">
              <span className="flag-stripe black"></span>
              <span className="flag-stripe red"></span>
              <span className="flag-stripe green"></span>
            </div>
            <p className="footer-note">© 2025 Kenya Finance Bill Awareness Platform |
              <span className="footer-disclaimer"> This summary highlights proposed provisions subject to parliamentary approval</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;